import { cloneElement, isValidElement, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { CSSProperties, ReactElement, ReactNode } from 'react';
import { Icon } from '../core/Icon';

export interface MenuItem {
    id?: string;
    label?: ReactNode;
    icon?: string;
    hint?: ReactNode;
    tone?: 'default' | 'danger';
    disabled?: boolean;
    /** Linha separadora. Os demais campos são ignorados. */
    divider?: boolean;
    onSelect?: () => void;
}

export interface MenuProps {
    /** Controle que abre o menu. Recebe onClick, aria-haspopup e aria-expanded. */
    trigger?: ReactNode;
    items?: MenuItem[];
    align?: 'left' | 'right';
    width?: number;
    onSelect?: (id: string) => void;
    style?: CSSProperties;
}

interface Posicao {
    top: number;
    left: number;
    maxHeight: number;
}

export function Menu({ trigger, items = [], align = 'right', width = 200, onSelect, style }: MenuProps) {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLSpanElement | null>(null);
    const gatilhoRef = useRef<HTMLSpanElement | null>(null);
    const painelRef = useRef<HTMLDivElement | null>(null);
    const [pos, setPos] = useState<Posicao | null>(null);

    /* O menu é levado para o `body` por portal, com posição calculada a partir do
       retângulo do gatilho.
       Dois motivos, os dois observados no produto: menu dentro de `Card`, de
       `DataTable` ou de qualquer contêiner com `overflow: hidden` era recortado; e
       `position: fixed` sozinho não resolve, porque ancestral com `transform` — a
       classe de entrada de tela tem — passa a ser o bloco de contenção do fixed e
       desloca o menu. */
    /* Mede a altura REAL do painel depois de montar e prende a posição dentro da
       viewport. Estimar altura por número de itens e derivar `bottom` do rect do
       gatilho produzia `bottom: -82px` e `max-height` maior que a tela quando a
       linha estava rolada para fora — o painel saía da tela e três itens ficavam
       inalcançáveis, sem rolagem para chegar neles. */
    useLayoutEffect(() => {
        if (!open) return;

        const medir = () => {
            const g = gatilhoRef.current;
            const p = painelRef.current;
            if (!g) return;
            const r = g.getBoundingClientRect();
            const vh = window.innerHeight;
            const vw = window.innerWidth;

            const tetoAltura = vh - 16;
            const alturaReal = p ? Math.min(p.scrollHeight, tetoAltura) : Math.min(items.length * 34 + 14, tetoAltura);

            /* Abaixo do gatilho por padrão; acima quando não cabe. Nos dois casos o
               resultado é PRESO na viewport, então gatilho fora da área visível
               encaixa o painel na tela em vez de acompanhá-lo para fora. */
            const cabeAbaixo = r.bottom + 6 + alturaReal <= vh - 8;
            const desejado = cabeAbaixo ? r.bottom + 6 : r.top - 6 - alturaReal;
            const top = Math.max(8, Math.min(desejado, vh - alturaReal - 8));
            const esquerda = align === 'left' ? r.left : r.right - width;

            setPos({
                top: Math.round(top),
                left: Math.round(Math.max(8, Math.min(esquerda, vw - width - 8))),
                maxHeight: Math.round(tetoAltura),
            });
        };

        medir();

        const aoClicarFora = (e: MouseEvent) => {
            const alvo = e.target as Node;
            if (ref.current?.contains(alvo)) return;
            if (painelRef.current?.contains(alvo)) return;
            setOpen(false);
        };
        const aoTeclar = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };

        document.addEventListener('mousedown', aoClicarFora);
        document.addEventListener('keydown', aoTeclar);
        window.addEventListener('resize', medir);
        window.addEventListener('scroll', medir, true);
        return () => {
            document.removeEventListener('mousedown', aoClicarFora);
            document.removeEventListener('keydown', aoTeclar);
            window.removeEventListener('resize', medir);
            window.removeEventListener('scroll', medir, true);
        };
    }, [open, align, width, items.length]);

    /* Ao fechar, a posição é descartada: reabrir noutra linha com a posição
       antiga produziria um salto visível no primeiro quadro. */
    useEffect(() => {
        if (!open) setPos(null);
    }, [open]);

    const alternar = () => setOpen((o) => !o);

    /* O gatilho recebe o clique nele mesmo, clonado. Envolvê-lo num <span
       onClick> punha o controle num elemento não interativo — sem foco, sem
       teclado — e escondia `aria-haspopup`/`aria-expanded` do controle real. */
    const propsDoGatilho = {
        onClick: alternar,
        'aria-haspopup': 'menu' as const,
        'aria-expanded': open,
    };
    const gatilho = isValidElement(trigger)
        ? cloneElement(trigger as ReactElement<Record<string, unknown>>, propsDoGatilho)
        : (
            <button type="button" {...propsDoGatilho} style={{ border: 0, background: 'transparent', padding: 0, cursor: 'pointer', display: 'inline-flex' }}>
                {trigger}
            </button>
        );

    const painel = open ? (
        <div
            ref={painelRef} role="menu"
            style={{
                /* Primeira passagem invisível: o painel precisa existir para ser medido,
                   e medir depois de pintar produziria um salto de posição. */
                position: 'fixed',
                top: pos ? pos.top : 0,
                left: pos ? pos.left : 0,
                visibility: pos ? 'visible' : 'hidden',
                zIndex: 80, width, maxHeight: pos ? pos.maxHeight : '90vh', overflowY: 'auto',
                background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)',
                boxShadow: 'var(--shadow-lg)', padding: 5,
                animation: 'vec-menu-in var(--dur-fast) var(--ease-entrance)',
            }}
        >
            {items.map((it, i) => (it.divider ? (
                <span key={`d${i}`} role="separator" style={{ display: 'block', height: 1, background: 'var(--border-subtle)', margin: '5px 0' }} />
            ) : (
                <button
                    key={it.id ?? i} type="button" role="menuitem" disabled={it.disabled}
                    onClick={() => {
                        setOpen(false);
                        if (it.id) onSelect?.(it.id);
                        it.onSelect?.();
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-secondary)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    style={{
                        display: 'flex', alignItems: 'center', gap: 9, width: '100%', padding: '7px 9px',
                        border: 0, background: 'transparent', borderRadius: 'var(--radius-xs)',
                        cursor: it.disabled ? 'not-allowed' : 'pointer', textAlign: 'left',
                        fontFamily: 'var(--font-ui)', fontSize: 13.5,
                        color: it.tone === 'danger' ? 'var(--danger-strong)' : 'var(--text-primary)',
                        opacity: it.disabled ? 0.45 : 1,
                    }}
                >
                    {it.icon && <Icon name={it.icon} size={15} style={{ color: it.tone === 'danger' ? 'var(--danger-strong)' : 'var(--neutral-600)' }} />}
                    <span style={{ flex: 1 }}>{it.label}</span>
                    {it.hint && <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{it.hint}</span>}
                </button>
            )))}
            <style>{'@keyframes vec-menu-in{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:none}}'}</style>
        </div>
    ) : null;

    return (
        <span ref={ref} style={{ position: 'relative', display: 'inline-flex', ...style }}>
            <span ref={gatilhoRef} style={{ display: 'inline-flex' }}>{gatilho}</span>
            {painel && typeof document !== 'undefined' ? createPortal(painel, document.body) : painel}
        </span>
    );
}
